import { Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    CONNECTION_ERROR_CODES,
    DataMaintenanceEquipmentModel,
    DataMaintenanceModel,
    DataMaintenanceSystemConfigP58Model,
    DataMaintenanceUtilities,
    IValueChanged,
    SynchronizationService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { of, Subject } from 'rxjs';
import { catchError, mergeMap, takeUntil } from 'rxjs/operators';
import { OdataDataMaintenanceService } from './odata-data-maintenance.service';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';

@Component({
    selector: 'pace-data-maintenance-sysconfig',
    templateUrl: './data-maintenance-sysconfig.component.html',
    styleUrls: ['./data-maintenance-sysconfig.component.scss'],
})
export class DataMaintenanceSysconfigComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    public model: DataMaintenanceModel | DataMaintenanceSystemConfigP58Model;

    public strings: {
        footer: string;
        technicalIdOrFunctionalLocationLabel: string;
    };

    public formChildren: FormArray;
    public dataMaintenanceSysConfigForm: FormGroup;
    public displayReasonText: boolean;
    public valueChanged = false;
    public isLoading = false;
    private destroyTrigger = new Subject<void>();
    public oldModel: DataMaintenanceSystemConfigP58Model;
    public orderByField: string;
    public reverseSort: boolean;
    public faSortDown = faSortDown;
    public faSortUp = faSortUp;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        private readonly odataDataMaintenanceService: OdataDataMaintenanceService,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly synchronizationService: SynchronizationService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly dataMaintenanceUtilities: DataMaintenanceUtilities,
    ) {
        this.displayReasonText = this.clientManager.isNgmAA();
        this.strings = {
            footer: this.clientManager.isNgmAA()
                ? <string>this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_SYSCONFIG_REPORT_TO_DATA_ADMIN')
                : <string>this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_SYSCONFIG_CHANGE_DATA'),
            technicalIdOrFunctionalLocationLabel: this.clientManager.isNgmAA()
                ? <string>this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_EQUIPMENT_FUNCLOC')
                : <string>this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_EQUIPMENT_TECHIDENT'),
        };
    }

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.dataMaintenanceSysConfigForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });

        if (this.model instanceof DataMaintenanceSystemConfigP58Model) {
            this.oldModel = _.cloneDeep(this.model);
        }

        this.orderByField = this.model.tableModel.options.sortColumn;
        this.reverseSort = this.model.tableModel.options.reverseSort;
        this.model.tableModel.selectByIndex(this.model.tableModel.options.initialSelectRow);
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj.model) {
            this.model = <DataMaintenanceModel | DataMaintenanceSystemConfigP58Model>changesObj.model.currentValue;
            this.orderByField = this.model.tableModel.options.sortColumn;
            this.reverseSort = this.model.tableModel.options.reverseSort;
            this.model.tableModel.selectByIndex(this.model.tableModel.options.initialSelectRow);
        }
    }

    public ngOnDestroy(): void {
        this.destroyTrigger.next();
    }

    // since we are using only one form and updating the fields every time we select a row,
    // Angular form status - pristine and dirty will not work as expected.
    // This function is an alternative.

    public modelValueChanged(event: IValueChanged): void {
        if (event.oldValue !== event.newValue) {
            this.valueChanged = true;
            if (this.dataMaintenanceSysConfigForm.invalid) {
                this.model.tableModel.selectedRow.rowValueInvalid = true;
            } else {
                this.model.tableModel.selectedRow.rowValueInvalid = false;
            }
        }
    }

    public checkRowValidity(): DataMaintenanceEquipmentModel | undefined {
        return this.model.tableModel.rows.find((item) => item.rowValueInvalid);
    }

    public doSort(field: string): void {
        this.orderByField = field;
        this.reverseSort = !this.reverseSort;
    }

    public submit(): void {
        if (this.model instanceof DataMaintenanceSystemConfigP58Model) {
            this.dataMaintenanceUtilities.openMailToSubmitData(this.model, this.oldModel, this.oldModel.isInvitro);
            this.modalInstance.hide();
            return;
        }
        this.isLoading = true;
        this.odataDataMaintenanceService
            .sendDataMaintenanceEquipment(this.model)
            .pipe(
                mergeMap(() => {
                    this.modalInstance.hide();
                    this.log.debug('DataMaintenanceSysconfigComponent -> sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.error(
                        `DataMaintenanceSysconfigComponent: submit: Error ${JSON.stringify(error)},
                        ${JSON.stringify(this.model.getOutModel())}`,
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                            : <string>this.translate.instant('NOTIFICATION_DETAIL_DATA_MAINTENANCE_SYNC_PROBLEM'),
                        error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                            ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                            : JSON.stringify(error),
                    );
                    this.isLoading = false;
                    return of(undefined);
                }),
                takeUntil(this.destroyTrigger),
            )
            .subscribe();
    }
}
